<template>
  <p>{{ $t("节点配置") }}</p>

  <!-- URL输入 -->
  <t-form-item name="url" :rules="urlRule">
    <template #label>
      <text-with-mark text="URL" />
    </template>
    <t-input v-model="formData.url" maxlength="200" placeholder="https://" />
  </t-form-item>

  <!-- 超时时间输入 -->
  <t-form-item name="callbackTimeout" :rules="callbackTimeoutRule">
    <template #label>
      <text-with-mark :text="$t('超时时间')" />
      <span style="color: #99a2b2; font-size: 12px; margin-left: 8px">
        ({{ $t("毫秒，默认4500ms") }})
      </span>
    </template>
    <t-input
      v-model.number="formData.callbackTimeout"
      :placeholder="$t('请输入')"
    />
  </t-form-item>

  <!-- 鉴权配置 -->
  <div class="label">
    <div style="margin-bottom: 8px">
      {{ $t("鉴权") }}
      <t-popup
        :content="$t('用于验证调用者身份有效性并校验其访问权限')"
        show-arrow
        destroy-on-close
      >
        <InfoCircleIcon class="infoIcon" />
      </t-popup>
    </div>
    <t-switch :value="!!formData.authType" @change="onAuthSwitchChange" />
  </div>

  <div v-if="!!formData.authType">
    <p style="margin-top: 8px; margin-bottom: 8px">
      <text-with-mark :text="$t('鉴权类型')" />
    </p>
    <t-form-item name="type" style="margin-bottom: 0px">
      <t-radio :checked="isBasicAuth" @change="handleBasicAuthChange">
        Basic Auth
      </t-radio>
      <t-radio
        :checked="isBearerAuth"
        style="margin-left: 16px"
        @change="handleBearerAuthChange"
      >
        Bearer
      </t-radio>
      <t-radio
        :checked="isCustomAuth"
        style="margin-left: 16px"
        @change="handleCustomAuthChange"
      >
        {{ $t("自定义") }}
      </t-radio>
    </t-form-item>

    <!-- Basic Auth -->
    <div v-if="formData.authType === 1">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Token" />
      </p>
      <t-form-item
        name="basicAuth.basicToken"
        :rules="requireRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.basicAuth.basicToken"
          maxlength="256"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>

    <!-- Bearer Token -->
    <div v-if="formData.authType === 2">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Token" />
      </p>
      <t-form-item
        name="bearerAuth.bearerToken"
        :rules="tokenRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.bearerAuth.bearerToken"
          maxlength="256"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>

    <!-- 自定义 Header/Query -->
    <div v-if="formData.authType === 3 || formData.authType === 4">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark :text="$t('密钥位置')" />
      </p>
      <t-radio-group
        :value="`${formData.authType}`"
        style="margin-bottom: 4px"
        @change="onAuthTypeChange"
      >
        <t-radio value="3">Header</t-radio>
        <t-radio value="4">Query</t-radio>
      </t-radio-group>

      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Key" />
      </p>
      <t-form-item
        name="customAuth.key"
        :rules="keyRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.customAuth.key"
          maxlength="1024"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Value" />
      </p>
      <t-form-item
        name="customAuth.value"
        :rules="requireRule"
        style="margin-bottom: 24px"
      >
        <t-input
          v-model="formData.customAuth.value"
          maxlength="1024"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>
  </div>

  <!-- 输入参数 -->
  <p style="margin-top: 24px">{{ $t("输入参数") }}</p>

  <div>
    <t-table
      v-if="(formData.params ?? []).length > 0"
      class="no-border-table"
      :data="formData.params"
      lazy-load
      size="small"
      :columns="paramsColumns"
      vertical-align="top"
    >
      <template #type="{ rowIndex }">
        <t-form-item :name="`formData.params[${rowIndex}].type`">
          <t-select
            v-model="formData.params[rowIndex].type"
            :options="paramTypeOptions"
            style="width: 150px"
            :placeholder="$t('请选择')"
            @change="onParamTypeChange(rowIndex, $event)"
          />
        </t-form-item>
      </template>
      <template #key="{ rowIndex, row }">
        <t-form-item
          :name="`formData.params[${rowIndex}].key`"
          :rules="getParamRules(rowIndex)"
        >
          <!-- type=1 信息收集变量 -->
          <t-select
            v-if="row.type === 1"
            v-model="formData.params[rowIndex].value"
            :options="infoExtractOptions"
            :placeholder="$t('选择变量')"
            :popup-props="selectPopupProps"
            @change="onInfoExtractVarChange(rowIndex, $event)"
          />
          <!-- type=2 系统变量 -->
          <t-select
            v-else-if="row.type === 2"
            v-model="formData.params[rowIndex].key"
            @change="onSystemVarChange(rowIndex, $event)"
          >
            <t-option
              v-for="item in systemVarsPool"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              <t-tooltip :content="item.tip">{{ item.label }}</t-tooltip>
            </t-option>
          </t-select>
          <!-- type=3 自定义变量（接口调用输出参数） -->
          <t-select
            v-else-if="row.type === 3"
            v-model="formData.params[rowIndex].value"
            :options="apiCallOptions"
            :placeholder="$t('选择变量')"
            :popup-props="selectPopupProps"
            @change="onCustomVarChange(rowIndex, $event)"
          />
          <!-- type=5 启动节点变量 -->
          <t-select
            v-else-if="row.type === 5"
            v-model="formData.params[rowIndex].value"
            :options="inputVariableOptions"
            :placeholder="$t('选择变量')"
            :popup-props="selectPopupProps"
            @change="onInputVariableChange(rowIndex, $event)"
          />
          <!-- type=4 固定变量 -->
          <div v-else-if="row.type === 4" class="fixedVar">
            <t-input
              v-model="formData.params[rowIndex].key"
              maxlength="200"
              placeholder="key"
              style="width: 105px"
            />
            <t-input
              v-model="formData.params[rowIndex].value"
              maxlength="200"
              placeholder="value"
              style="width: 105px"
            />
          </div>
        </t-form-item>
      </template>
      <template #operation="{ rowIndex }">
        <CloseCircleIcon
          size="16"
          style="cursor: pointer; vertical-align: bottom"
          @click="deleteParam(rowIndex)"
        />
      </template>
    </t-table>
    <t-form-item :status-icon="false">
      <div v-if="(formData.params?.length ?? 0) < 20">
        <t-link theme="primary" hover="color" @click="addParam">
          {{ $t("添加输入参数") }}
        </t-link>
      </div>
    </t-form-item>
  </div>

  <t-divider style="margin: 8px 0"></t-divider>

  <!-- 输出参数 -->
  <p style="margin-top: 24px; margin-bottom: 8px">{{ $t("输出参数") }}</p>
  <div v-if="(formData.returns ?? []).length > 0">
    <p style="color: #6d778c; margin-bottom: 8px">{{ $t("参数") }}</p>

    <div v-for="(item, index) in formData.returns" :key="index">
      <t-form-item
        :name="`formData.returns[${index}].key`"
        style="margin-bottom: 10px"
      >
        <!-- 使用 varIdList 作为数据源，只显示当前节点定义的变量 -->
        <t-select
          v-model="formData.returns[index].key"
          :placeholder="$t('选择变量')"
          :popup-props="selectPopupProps"
          @change="onReturnVarChange(index, $event)"
          @popupVisibleChange="(visible) => onSelectPopupChange(visible)"
        >
          <t-option
            v-for="option in currentNodeVarOptions"
            :key="option.id"
            :value="option.value"
            :label="option.label"
            class="custom-option"
            @mouseenter="() => showEditButton(option)"
            @mouseleave="() => hideEditButton(option)"
          >
            <div
              style="display: flex; justify-content: space-between; width: 100%"
            >
              <span>{{ option.label }}</span>
              <div v-if="option.showButton" style="display: flex; gap: 5px">
                <img
                  :src="editNodeIcon"
                  class="custom-button"
                  @click.stop="handleEditVar(index, option)"
                />
                <img
                  :src="deleteNodeIcon"
                  class="custom-button"
                  @click.stop="handleDeleteVar(index, option)"
                />
              </div>
            </div>
          </t-option>
          <t-option @click.stop="handleCreateVar(index)">
            <span style="padding: 0px; color: #0052d9">
              {{ $t("新建变量") }}
            </span>
          </t-option>
        </t-select>
        <CloseCircleIcon
          size="16"
          style="cursor: pointer; margin-left: 15px; margin-top: -2px"
          @click="deleteReturn(index)"
        />
      </t-form-item>
    </div>
  </div>

  <!-- 变量编辑对话框 -->
  <t-dialog
    v-model:visible="varDialogVisible"
    width="300"
    :header="false"
    @confirm="handleVarDialogConfirm"
  >
    <p v-if="varDialogData.type === 'delete'">{{ $t("确认删除？") }}</p>
    <div v-else>
      <t-input
        v-model="varDialogData.value"
        :placeholder="$t('请输入变量key')"
        :status="varDialogErrorMessage ? 'error' : 'default'"
        maxlength="100"
        @input="varDialogErrorMessage = ''"
      />
      <div v-if="varDialogErrorMessage" class="error-message">
        {{ varDialogErrorMessage }}
      </div>
    </div>
  </t-dialog>

  <div v-if="(formData.returns?.length ?? 0) < 20" style="margin-top: 10px">
    <t-link theme="primary" hover="color" @click="addReturn">
      {{ $t("添加输出参数") }}
    </t-link>
  </div>

  <!-- 异常处理 -->
  <div class="label">
    <div style="margin: 14px 0">
      <span>{{ $t("异常处理") }}</span>
      <t-popup
        :content="
          $t(
            '开启后，接口调用节点将新增异常处理分支。若调用失败，任务流自动进入该分支。',
          )
        "
        show-arrow
        destroy-on-close
      >
        <InfoCircleIcon class="infoIcon" />
      </t-popup>
    </div>
    <t-switch :value="!!formData.fallbackSwitch" @change="onSwitchChange" />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  reactive,
  watch,
  inject,
  ref,
  getCurrentInstance,
  nextTick,
  type Ref,
} from "vue";
import { CloseCircleIcon, InfoCircleIcon } from "tdesign-icons-vue-next";
import { cloneDeep, trim } from "lodash-es";
import { systemVarsPool } from "@/utils/const";
import editNodeIcon from "@/assets/icons/edit-node-icon.svg";
import deleteNodeIcon from "@/assets/icons/delete-node-icon.svg";
import {
  validateBearerToken,
  validateHeaderKey,
  validateQueryKey,
  validateUrl,
} from "@/utils/rules";
import { t } from "@/utils/i18n";
import { getRelationUUID, getUUID } from "@/utils";
import { FALLBACK_ERROR, FALLBACK_SUCCESS } from "../const";
import { extractInfoNodesData } from "@/flow/flow-util/node/utils";

const emit = defineEmits(["updateData"]);

const variablePool = inject<any>("variablePool", { value: [] });
const xGraph = inject("xGraph");
const varIdList = inject<
  Ref<Array<{ id: string; nodeId: string; varName: string }>>
>("varIdList", ref([]));
const inputVarList = inject<Ref<Array<{ name: string; type: string }>>>(
  "inputVarList",
  ref([]),
);

// 信息提取节点的变量映射：优先 en_name，最后 name -> info_id
const infoNameToIdMap = ref<Record<string, string>>({});
const customVarToIdMap = ref<Record<string, string>>({});
const infoExtractOptions = ref<
  Array<{
    value: string;
    label: string;
    tip: string;
    variableKey: string;
  }>
>([]);
const apiCallOptions = ref<
  Array<{
    value: string;
    label: string;
    tip: string;
  }>
>([]);
const inputVariableOptions = ref<Array<{ value: string; label: string }>>([]);

const props = defineProps<{
  data: {
    url?: string;
    callbackTimeout?: number;
    params?: Array<{
      type: number;
      key: string;
      value?: string;
    }>;
    returns?: Array<{
      variable_id: string;
      key: string;
    }>;
    fallbackSwitch?: number;
    branches?: Array<{ content: string; id: string; type: 0 | 1 }>;
    authType?: number;
    basicAuth?: {
      basicToken?: string;
    };
    bearerAuth?: {
      bearerToken?: string;
    };
    customAuth?: {
      key: string;
      value: string;
    };
  };
  currentNodeId?: string;
}>();

const currentNodeVarOptions = computed(() => {
  if (!props.currentNodeId) {
    return [];
  }
  return varIdList.value
    .filter((v) => v.nodeId === props.currentNodeId)
    .map((v) => ({
      id: v.id,
      value: v.varName,
      label: v.varName,
      showButton: false,
    }));
});

const varDialogVisible = ref(false);
const varDialogData = reactive<{
  type: "create" | "edit" | "delete";
  value: string;
  index: number;
  option?: { id: string; value: string; label: string };
}>({
  type: "create",
  value: "",
  index: -1,
});
const varDialogErrorMessage = ref("");

const selectPopupProps = {
  overlayInnerStyle: (trigger: HTMLElement) => ({
    width: `${trigger.offsetWidth}px`,
  }),
};

const formData = reactive({
  url: props.data.url ?? "",
  callbackTimeout: props.data.callbackTimeout ?? 4500,
  params: cloneDeep(props.data.params) ?? [
    {
      type: 4,
      key: "",
      value: "",
    },
  ],
  returns: cloneDeep(props.data.returns) ?? [
    {
      variable_id: "",
      key: "",
    },
  ],
  fallbackSwitch: props.data.fallbackSwitch ?? 0,
  branches:
    props.data.branches && props.data.branches.length > 0
      ? cloneDeep(props.data.branches)
      : [
          {
            id: getRelationUUID(),
            content: t("调用成功"),
            type: FALLBACK_SUCCESS,
          },
        ],
  authType: props.data.authType ?? 0,
  basicAuth: cloneDeep(props.data.basicAuth) ?? { basicToken: "" },
  bearerAuth: cloneDeep(props.data.bearerAuth) ?? { bearerToken: "" },
  customAuth: cloneDeep(props.data.customAuth) ?? { key: "", value: "" },
  async: false,
});

const urlRule = [
  {
    validator: (url) => !!trim(url),
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (url) => validateUrl(url),
    message: t("请输入正确url地址"),
    trigger: "change",
  },
];

const requireRule = [
  {
    validator: (val) => !!trim(val),
    message: t("不能为空"),
    trigger: "change",
  },
];

const tokenRule = [
  {
    validator: (token) => !!trim(token),
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (token) => validateBearerToken(token),
    message: t("格式错误"),
    trigger: "change",
  },
];

const keyRule = [
  {
    validator: (key) => !!trim(key),
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (key) => {
      return formData.authType === 3
        ? validateHeaderKey(key)
        : validateQueryKey(key);
    },
    message: t("格式错误"),
    trigger: "change",
  },
];

const callbackTimeoutRule = [
  {
    validator: (value) => {
      if (value === null || value === undefined || value === "") {
        return true;
      }
      const numValue = Number(value);
      if (isNaN(numValue) || numValue <= 0) {
        return false;
      }
      return numValue <= 30000;
    },
    message: t("超时时间不能超过30秒（30000毫秒）"),
    trigger: "change",
  },
];

const clearAllAuthFields = () => {
  formData.basicAuth.basicToken = "";
  formData.bearerAuth.bearerToken = "";
  formData.customAuth.key = "";
  formData.customAuth.value = "";
};

const resetFormValidation = async () => {
  await nextTick();
  try {
    const instance = getCurrentInstance();
    if (instance) {
      let parent = instance.parent;
      while (parent) {
        const setupState = (parent as any).setupState;
        const exposed = (parent as any).exposed;
        const refs = (parent as any).refs;

        const formRef =
          setupState?.formRef || exposed?.formRef || refs?.formRef;

        if (formRef && typeof formRef.clearValidate === "function") {
          formRef.clearValidate([
            "basicAuth.basicToken",
            "bearerAuth.bearerToken",
            "customAuth.key",
            "customAuth.value",
          ]);
          break;
        }

        parent = parent.parent;
      }
    }
  } catch (error) {
    console.warn("无法重置表单验证状态:", error);
  }
};

const onAuthSwitchChange = async (value) => {
  formData.authType = value ? 2 : 0;
  clearAllAuthFields();
  await resetFormValidation();
};

const isBasicAuth = computed(() => formData.authType === 1);
const isBearerAuth = computed(() => formData.authType === 2);
const isCustomAuth = computed(() => formData.authType > 2);

const handleBasicAuthChange = async (checked) => {
  if (checked) {
    formData.authType = 1;
    clearAllAuthFields();
    await resetFormValidation();
  }
};

const handleBearerAuthChange = async (checked) => {
  if (checked) {
    formData.authType = 2;
    clearAllAuthFields();
    await resetFormValidation();
  }
};

const handleCustomAuthChange = async (checked) => {
  if (checked) {
    formData.authType = 4;
    clearAllAuthFields();
    await resetFormValidation();
  }
};

const onAuthTypeChange = async (value) => {
  formData.authType = +value;
  clearAllAuthFields();
  await resetFormValidation();
};

const paramTypeOptions = [
  { value: 1, label: t("信息收集变量") },
  { value: 2, label: t("系统变量") },
  { value: 3, label: t("自定义变量") },
  { value: 5, label: t("启动节点变量") },
  { value: 4, label: t("固定变量") },
];

const paramsColumns = [
  {
    colKey: "type",
    title: t("参数类型"),
    width: 165,
  },
  {
    colKey: "key",
    title: t("参数"),
  },
  {
    colKey: "operation",
    width: 30,
    align: "right",
  },
];

const getParamRules = (index: number) => [
  {
    validator: () => {
      const param = formData.params[index];
      if (!param) {
        return true;
      }

      return !!trim(param.key ?? "") && !!trim(param.value ?? "");
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

const onParamTypeChange = (index, value) => {
  formData.params[index].type = value;
  formData.params[index].key = "";
  formData.params[index].value = "";
};

const onInfoExtractVarChange = (index, info_id) => {
  // 从 infoExtractOptions 中找到对应的数据源 key 作为 key
  const option = infoExtractOptions.value.find((opt) => opt.value === info_id);
  if (option) {
    formData.params[index].key = option.variableKey;
    formData.params[index].value = info_id;
  } else {
    formData.params[index].key = "";
    formData.params[index].value = "";
  }
};

const onSystemVarChange = (index, value) => {
  formData.params[index].key = value;
  formData.params[index].value = value;
};

const onCustomVarChange = (index, info_id) => {
  const option = apiCallOptions.value.find((opt) => opt.value === info_id);
  if (option) {
    const varName = option.label.split("/").pop() || option.label;
    formData.params[index].key = varName;
    formData.params[index].value = info_id;
  } else {
    formData.params[index].key = "";
    formData.params[index].value = "";
  }
};

const onInputVariableChange = (index, variableName) => {
  formData.params[index].key = variableName;
  formData.params[index].value = variableName;
};

const addParam = () => {
  formData.params.push({
    type: 4,
    key: "",
    value: "",
  });
};

const deleteParam = (index: number) => {
  formData.params.splice(index, 1);
};

const showEditButton = (option) => {
  option.showButton = true;
};
const hideEditButton = (option) => {
  option.showButton = false;
};

const onSelectPopupChange = (visible) => {
  if (!visible) {
    currentNodeVarOptions.value.forEach((opt) => {
      opt.showButton = false;
    });
  }
};

const handleCreateVar = (index) => {
  varDialogData.type = "create";
  varDialogData.value = "";
  varDialogData.index = index;
  varDialogErrorMessage.value = "";
  varDialogVisible.value = true;
};

const handleEditVar = (index, option) => {
  varDialogData.type = "edit";
  varDialogData.value = option.label;
  varDialogData.index = index;
  varDialogData.option = option;
  varDialogErrorMessage.value = "";
  varDialogVisible.value = true;
};

const handleDeleteVar = (index, option) => {
  varDialogData.type = "delete";
  varDialogData.index = index;
  varDialogData.option = option;
  varDialogVisible.value = true;
};

const handleVarDialogConfirm = () => {
  const { type, value, index, option } = varDialogData;
  const trimmedValue = trim(value);

  if (type === "delete") {
    if (option && option.id && props.currentNodeId) {
      const varIndex = varIdList.value.findIndex(
        (v) => v.id === option.id && v.nodeId === props.currentNodeId,
      );
      if (varIndex !== -1) {
        varIdList.value.splice(varIndex, 1);
      }
      formData.returns.forEach((returnItem) => {
        if (returnItem.variable_id === option.id) {
          returnItem.key = "";
          returnItem.variable_id = "";
        }
      });
      formData.params.forEach((param) => {
        if (param.type === 3 && param.value === option.id) {
          param.value = "";
          param.key = "";
        }
      });
    }
    varDialogVisible.value = false;
    return;
  }

  if (!trimmedValue) {
    varDialogErrorMessage.value = t("不能为空");
    return;
  }

  const isSystemVarName = systemVarsPool.some(
    (sysVar) => sysVar.value === trimmedValue,
  );
  if (isSystemVarName) {
    varDialogErrorMessage.value = t("该变量名称已存在，请尝试其他名称");
    return;
  }

  if (
    type === "create" ||
    (type === "edit" && option && option.value !== trimmedValue)
  ) {
    const existingVar = varIdList.value.find(
      (v) => v.nodeId === props.currentNodeId && v.varName === trimmedValue,
    );
    if (existingVar) {
      varDialogErrorMessage.value = t("该变量名称已存在，请尝试其他名称");
      return;
    }
  }

  varDialogErrorMessage.value = "";

  if (type === "create") {
    onReturnVarChange(index, trimmedValue);
  } else if (type === "edit" && option) {
    const existingVar = varIdList.value.find(
      (v) => v.id === option.id && v.nodeId === props.currentNodeId,
    );
    if (existingVar) {
      existingVar.varName = trimmedValue;
      formData.returns[index].key = trimmedValue;
      formData.params.forEach((param) => {
        if (param.type === 3 && param.value === option.id) {
          const varInfo = varIdList.value.find((v) => v.id === option.id);
          if (varInfo) {
            param.key = varInfo.varName;
          }
        }
      });
    }
  }

  varDialogVisible.value = false;
};

const onReturnVarChange = (index, newKey) => {
  if (!props.currentNodeId) {
    console.warn("currentNodeId is missing");
    return;
  }

  if (!newKey) {
    return;
  }

  const currentVariableId = formData.returns[index].variable_id;

  if (currentVariableId) {
    const existingVar = varIdList.value.find(
      (v) => v.id === currentVariableId && v.nodeId === props.currentNodeId,
    );
    if (existingVar) {
      existingVar.varName = newKey;
    } else {
      varIdList.value.push({
        id: currentVariableId,
        nodeId: props.currentNodeId,
        varName: newKey,
      });
    }
  } else {
    const newVariableId = `userdefine-${getUUID()}`;

    varIdList.value.push({
      id: newVariableId,
      nodeId: props.currentNodeId,
      varName: newKey,
    });

    formData.returns[index].variable_id = newVariableId;
    formData.returns[index].key = newKey;
  }
};

const addReturn = () => {
  formData.returns.push({
    variable_id: "",
    key: "",
  });
};

const deleteReturn = (index: number) => {
  const returnItem = formData.returns[index];

  if (returnItem.variable_id && props.currentNodeId) {
    const varIndex = varIdList.value.findIndex(
      (v) =>
        v.id === returnItem.variable_id && v.nodeId === props.currentNodeId,
    );
    if (varIndex !== -1) {
      varIdList.value.splice(varIndex, 1);
    }
  }

  if (returnItem.variable_id) {
    formData.params.forEach((param) => {
      if (param.type === 3 && param.value === returnItem.variable_id) {
        param.value = "";
        param.key = "";
      }
    });
  }

  formData.returns.splice(index, 1);
};

const onSwitchChange = (value) => {
  formData.fallbackSwitch = value ? 1 : 0;
  if (value) {
    formData.branches = [
      { id: getRelationUUID(), content: t("调用成功"), type: FALLBACK_SUCCESS },
      { id: getRelationUUID(), content: t("异常处理"), type: FALLBACK_ERROR },
    ];
  } else {
    formData.branches = [
      { id: getRelationUUID(), content: t("调用成功"), type: FALLBACK_SUCCESS },
    ];
  }
};

watch(
  () => variablePool.value,
  (newVal, oldVal) => {
    if (!Array.isArray(newVal)) return;
    const oldVars = Array.isArray(oldVal) ? oldVal : [];
    const newVars = newVal.filter((v) => !oldVars.includes(v));
    newVars.forEach((varName) => {
      if (!customVarToIdMap.value[varName]) {
        customVarToIdMap.value[varName] = `userdefine-${getUUID()}`;
      }
    });
  },
  { immediate: true },
);

watch(
  [xGraph as any, () => props.currentNodeId, inputVarList],
  ([newVal, currentNodeId]) => {
    if (!newVal) return;

    const nodes = newVal.getNodes();
    const infoNodesData = extractInfoNodesData(nodes);

    // 建立信息字段 key -> info_id 的映射，优先 en_name，最后 name
    const map: Record<string, string> = {};
    infoNodesData.forEach((node) => {
      node.infos.forEach((info) => {
        const infoKey = info.en_name || info.name;
        if (infoKey) {
          map[infoKey] = info.id;
        }
      });
    });

    infoNameToIdMap.value = map;

    const apiCallNodesData: Array<{
      nodeName: string;
      returns: Array<{ variable_id: string; key: string }>;
    }> = [];
    nodes.forEach((node) => {
      const nodeModel = node.getModel ? node.getModel() : node;
      const nodeData = nodeModel.nodeData;
      if (
        nodeData.nodeType === "interfaceInvocationNode" &&
        nodeData.returns &&
        Array.isArray(nodeData.returns) &&
        (!currentNodeId || nodeModel.id !== currentNodeId)
      ) {
        apiCallNodesData.push({
          nodeName: nodeData.nodeName || "",
          returns: nodeData.returns.filter(
            (r) =>
              r.variable_id && r.key && r.variable_id.startsWith("userdefine-"),
          ),
        });
      }
    });

    infoExtractOptions.value = infoNodesData
      .map((node) => {
        return node.infos.map((info) => ({
          value: info.id,
          label: node.nodeName + "/" + info.name,
          tip: node.nodeName,
          variableKey: info.en_name || info.name,
        }));
      })
      .flat();

    apiCallOptions.value = apiCallNodesData
      .map((node) => {
        return node.returns.map((ret) => {
          const varInfo = varIdList.value.find((v) => v.id === ret.variable_id);
          const varName = varInfo ? varInfo.varName : ret.key;

          return {
            value: ret.variable_id,
            label: node.nodeName + "/" + varName,
            tip: node.nodeName,
          };
        });
      })
      .flat();

    const inputVariableSet = new Set<string>();
    const nextInputVariableOptions: Array<{ value: string; label: string }> = [];
    const appendInputVariable = (name?: string) => {
      const normalized = trim(name ?? "");
      if (!normalized || inputVariableSet.has(normalized)) {
        return;
      }
      inputVariableSet.add(normalized);
      nextInputVariableOptions.push({
        value: normalized,
        label: normalized,
      });
    };

    nodes.forEach((node) => {
      const nodeModel = node.getModel ? node.getModel() : node;
      const nodeData = nodeModel.nodeData;
      if (
        nodeData.nodeType === "userIntentNode" &&
        Array.isArray(nodeData.inputVariables)
      ) {
        nodeData.inputVariables.forEach((item) => {
          appendInputVariable(item.name);
        });
      }
    });

    (inputVarList.value ?? []).forEach((item) => {
      appendInputVariable(item.name);
    });

    inputVariableOptions.value = nextInputVariableOptions;

    formData.returns.forEach((returnItem) => {
      if (returnItem.key && !returnItem.variable_id && currentNodeId) {
        const existingVar = varIdList.value.find(
          (v) => v.nodeId === currentNodeId && v.varName === returnItem.key,
        );

        if (existingVar) {
          returnItem.variable_id = existingVar.id;
        } else {
          const variable_id = `userdefine-${getUUID()}`;
          varIdList.value.push({
            id: variable_id,
            nodeId: currentNodeId,
            varName: returnItem.key,
          });
          returnItem.variable_id = variable_id;
        }
      }
    });

    formData.params.forEach((param) => {
      if (param.type === 1) {
        if (param.key && (!param.value || !param.value.startsWith("info-"))) {
          const info_id = map[param.key];
          if (info_id) {
            param.value = info_id;
          }
        }
        if (param.value && param.value.startsWith("info-")) {
          const exists = infoExtractOptions.value.some(
            (opt) => opt.value === param.value,
          );
          if (!exists) {
            param.value = "";
            param.key = "";
          }
        }
      } else if (param.type === 3) {
        if (
          param.key &&
          (!param.value || !param.value.startsWith("userdefine-"))
        ) {
          const option = apiCallOptions.value.find(
            (opt) => opt.label.split("/").pop() === param.key,
          );
          if (option) {
            param.value = option.value;
          }
        }
        if (param.value && param.value.startsWith("userdefine-")) {
          const exists = apiCallOptions.value.some(
            (opt) => opt.value === param.value,
          );
          if (!exists) {
            param.value = "";
            param.key = "";
          }
        }
      } else if (param.type === 5) {
        const candidate = trim(param.value ?? "") || trim(param.key ?? "");
        if (candidate && inputVariableSet.has(candidate)) {
          param.key = candidate;
          param.value = candidate;
        } else if (candidate) {
          param.key = "";
          param.value = "";
        }
      }
    });
  },
  { immediate: true, deep: true },
);

watch(
  formData,
  (newVal) => {
    if (!newVal.callbackTimeout || newVal.callbackTimeout <= 0) {
      newVal.callbackTimeout = 4500;
    }

    emit("updateData", newVal);
  },
  { deep: true },
);
</script>

<style lang="less" scoped>
:deep(.flow-t-table td) {
  border: none;
}
:deep(.flow-t-table th) {
  border: none;
}
:deep(.flow-t-table.flow-t-size-s th) {
  padding-left: 0px;
  padding-top: 6px;
  padding-bottom: 6px;
}
:deep(.flow-t-table.flow-t-size-s td) {
  padding: 0px;
}
:deep(.flow-t-table tr) {
  height: 42px;
}
:deep(.flow-t-table .flow-t-table__header tr) {
  height: 30px;
}
.fixedVar {
  display: flex;
  flex: 1 1;
  justify-content: space-between;
}
.label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.infoIcon {
  color: #99a2b2;
  margin-top: -3px;
  margin-left: 4px;
}
.custom-option {
  display: flex;
  width: 100%;
  justify-content: space-between;
}
.custom-button {
  vertical-align: middle;
  cursor: pointer;
  margin-left: 5px;
  width: 16px;
  height: 16px;
}
.error-message {
  color: var(--td-error-color);
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
}
</style>
